import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useTotalUnreadCount() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: count = 0 } = useQuery({
    queryKey: ["total-unread", user?.id],
    queryFn: async () => {
      // DM unread
      const { count: dmCount } = await supabase
        .from("direct_messages")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user!.id)
        .not("deleted_by", "cs", `{${user!.id}}`)
        .is("read_at", null);

      // Training unread
      const { count: trainingCount } = await supabase
        .from("training_messages" as any)
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user!.id)
        .is("read_at", null);

      // Group chat unread — count groups with messages after last_read_at
      // Get user's groups
      const { data: memberGroups } = await supabase
        .from("group_members")
        .select("group_id")
        .or(`player_id.eq.${user!.id},parent_id.eq.${user!.id}`)
        .eq("status", "active");
      const { data: coachGroups } = await supabase
        .from("training_groups")
        .select("id")
        .eq("coach_id", user!.id);
      const groupIds = [...new Set([
        ...(memberGroups ?? []).map((r: any) => r.group_id),
        ...(coachGroups ?? []).map((r: any) => r.id),
      ])];

      let groupUnread = 0;
      if (groupIds.length > 0) {
        const { data: readStates } = await supabase
          .from("message_read_state" as any)
          .select("source_id, last_read_at")
          .eq("user_id", user!.id)
          .eq("conversation_type", "group_chat")
          .in("source_id", groupIds);
        const readMap = new Map((readStates ?? []).map((r: any) => [r.source_id, r.last_read_at]));

        // For each group, check if latest message is after last_read_at
        const { data: latestMsgs } = await supabase
          .from("group_chat_messages")
          .select("group_id, sender_id, created_at")
          .in("group_id", groupIds)
          .order("created_at", { ascending: false });
        const lastMsgMap = new Map<string, any>();
        for (const m of latestMsgs ?? []) {
          if (!lastMsgMap.has(m.group_id)) lastMsgMap.set(m.group_id, m);
        }
        for (const gid of groupIds) {
          const lastMsg = lastMsgMap.get(gid);
          if (!lastMsg || lastMsg.sender_id === user!.id) continue;
          const lastRead = readMap.get(gid);
          if (!lastRead || lastMsg.created_at > lastRead) groupUnread++;
        }
      }

      return (dmCount ?? 0) + (trainingCount ?? 0) + groupUnread;
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  // Realtime: refetch on new messages
  useEffect(() => {
    if (!user) return;
    const channels = [
      supabase.channel("unread-rt-dm").on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_messages" }, () => qc.invalidateQueries({ queryKey: ["total-unread"] })),
      supabase.channel("unread-rt-training").on("postgres_changes", { event: "INSERT", schema: "public", table: "training_messages" }, () => qc.invalidateQueries({ queryKey: ["total-unread"] })),
      supabase.channel("unread-rt-group-total").on("postgres_changes", { event: "INSERT", schema: "public", table: "group_chat_messages" }, () => qc.invalidateQueries({ queryKey: ["total-unread"] })),
      supabase.channel("unread-rt-match-total").on("postgres_changes", { event: "INSERT", schema: "public", table: "match_chat_messages" }, () => qc.invalidateQueries({ queryKey: ["total-unread"] })),
    ];
    channels.forEach((c) => c.subscribe());
    return () => { channels.forEach((c) => supabase.removeChannel(c)); };
  }, [user, qc]);

  return count;
}
